/**
 * Prev/Next Navigation Component
 *
 * Simple navigation with previous and next buttons.
 * Supports min/max values for bounded navigation.
 *
 * @package oska
 */

/**
 * PrevNextNav class handles navigation between items
 */
class PrevNextNav {
  /**
   * @param {HTMLElement} element - The root .prev-next-nav element
   */
  constructor(element) {
    this.root = element;
    this.prevBtn = element.querySelector('[data-direction="prev"]');
    this.nextBtn = element.querySelector('[data-direction="next"]');

    // Get configuration from data attributes
    this.min = parseInt(element.dataset.min, 10) || null;
    this.max = parseInt(element.dataset.max, 10) || null;
    this.current = parseInt(element.dataset.current, 10) || null;

    // Find associated display element (sibling with data-prev-next-display)
    this.displayEl = element.parentElement?.querySelector('[data-prev-next-display]');

    this.init();
  }

  /**
   * Initialize event listeners
   */
  init() {
    // Update button states based on current value
    this.updateButtonStates();

    // Prev button click
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.navigate('prev'));
    }

    // Next button click
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.navigate('next'));
    }

    // Keyboard support when focused on container
    this.root.addEventListener('keydown', (e) => this.handleKeydown(e));
  }

  /**
   * Navigate in the specified direction
   * @param {'prev' | 'next'} direction
   */
  navigate(direction) {
    // If we have numeric min/max values, handle bounded navigation
    if (this.current !== null) {
      if (direction === 'prev' && (this.min === null || this.current > this.min)) {
        this.current--;
      } else if (direction === 'next' && (this.max === null || this.current < this.max)) {
        this.current++;
      }

      // Update display
      this.updateDisplay();
      // Update button states
      this.updateButtonStates();
      // Update data attribute
      this.root.dataset.current = this.current;
    }

    // Dispatch custom event
    this.dispatchNavigateEvent(direction);
  }

  /**
   * Update the display element with current value
   */
  updateDisplay() {
    if (this.displayEl && this.current !== null) {
      this.displayEl.textContent = this.current;
    }
  }

  /**
   * Update button disabled states based on min/max bounds
   */
  updateButtonStates() {
    if (this.current === null) return;

    // Disable prev button at minimum
    if (this.prevBtn && this.min !== null) {
      this.prevBtn.disabled = this.current <= this.min;
    }

    // Disable next button at maximum
    if (this.nextBtn && this.max !== null) {
      this.nextBtn.disabled = this.current >= this.max;
    }
  }

  /**
   * Dispatch custom navigation event
   * @param {'prev' | 'next'} direction
   */
  dispatchNavigateEvent(direction) {
    const event = new CustomEvent('prev-next-nav:navigate', {
      detail: {
        direction,
        current: this.current,
      },
      bubbles: true,
    });
    this.root.dispatchEvent(event);
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} e
   */
  handleKeydown(e) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.navigate('prev');
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.navigate('next');
        break;
    }
  }

  /**
   * Get current value
   * @returns {number|null}
   */
  getCurrent() {
    return this.current;
  }

  /**
   * Set current value programmatically
   * @param {number} value
   */
  setCurrent(value) {
    if (this.min !== null && value < this.min) {
      value = this.min;
    }
    if (this.max !== null && value > this.max) {
      value = this.max;
    }
    this.current = value;
    this.updateDisplay();
    this.updateButtonStates();
    this.root.dataset.current = this.current;
  }
}

/**
 * Initialize all prev/next navigation components on the page
 */
function initPrevNextNav() {
  const navs = document.querySelectorAll('[data-prev-next-nav]');
  const instances = [];

  navs.forEach((nav) => {
    instances.push(new PrevNextNav(nav));
  });

  return instances;
}

export default initPrevNextNav;
export { PrevNextNav };
